import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { COMPLAINTS_DETAIL, COMPLAINTS_LIST, COMPLAINTS_UPDATE, GRIEVANCES_LIST, CHAPTER_LIST_DROPDOWN } from "../../routes/routes";
import { COMPLAINT_UPDATED } from "../../constant/constant";
import { hasRole, NoDataFound, ValidationMessage } from "../../components/Utils";
import { glassSelectStyles } from "../../constant/selectStyles";
import { usePermission } from "../../hooks/usePermission";
import Loader from "../../components/common/Loader";
import StickyTable from "../../components/common/StickyTable";
import { applyExcelThinBorders, styleExcelHeaderRow } from "../../utils/excelStyles";

const authUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch (error) {
    return {};
  }
};

const statusOptions = [
  { value: "", label: "All statuses" },
  { value: "1", label: "Open" },
  { value: "2", label: "In review" },
  { value: "3", label: "Closed" },
];

const reviewStatusOptions = statusOptions.filter((option) => option.value !== "");

const ComplaintsList = () => {
  const { hasPermission } = usePermission();
  const location = useLocation();
  const isGrievance = location.pathname.startsWith("/grievances");
  const caseType = isGrievance ? 2 : 1;
  const title = isGrievance ? "Grievances" : "Complaints";
  const user = authUser();
  const isChapterAdmin = hasRole([3]);
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState(isChapterAdmin ? user.chapter_id : "");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [outcome, setOutcome] = useState("");
  const [nextStatus, setNextStatus] = useState("1");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isChapterAdmin) return;
    axios.get(CHAPTER_LIST_DROPDOWN).then((res) => {
      if (res.status === 200 && res.data.success) setChapters(res.data.data || []);
    });
  }, [isChapterAdmin]);

  const load = () => {
    setLoading(true);
    axios.get(isGrievance ? GRIEVANCES_LIST : COMPLAINTS_LIST, {
      params: {
        chapter_id: chapterId || undefined,
        status: status || undefined,
        search,
        isAdmin: 1,
        paging_type: "nopaginate",
      },
    }).then((res) => {
      if (res.status === 200 && res.data.success) {
        setRows(isGrievance ? res.data.data?.grievances || [] : res.data.data?.complaints || []);
      }
    }).catch((err) => {
      toast.error(err.response?.data?.msg || `Unable to load ${title.toLowerCase()}`);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, status, search, isGrievance]);

  const openCase = (id) => {
    axios.get(COMPLAINTS_DETAIL + id).then((res) => {
      if (res.status === 200 && res.data.success) {
        setSelected(res.data.data);
        setOutcome(res.data.data.outcome_note || "");
        setNextStatus(String(res.data.data.status));
        setErrors({});
      }
    });
  };

  const visibleRows = rows.filter((row) => Number(row.case_type) === caseType);

  const exportExcel = async () => {
    if (!visibleRows.length) {
      toast.error("No data found!.");
      return;
    }
    const columns = isGrievance
      ? ["S.No", "Reference", "Chapter", "Filed by", "Note", "Status", "Outcome note", "Date"]
      : ["S.No", "Reference", "Chapter", "Filed by", "About", "Note", "Status", "Outcome note", "Date"];
    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet(title);
    worksheet.addRow(columns);
    styleExcelHeaderRow(worksheet.getRow(1), columns.length);
    visibleRows.forEach((row, index) => {
      const values = [
        index + 1,
        row.reference_no || "",
        row.chapter_name || "",
        row.filed_by_name || "",
      ];
      if (!isGrievance) values.push(row.about_user_name || "");
      values.push(row.note || "", row.status_text || "", row.outcome_note || "", row.created_at || "");
      worksheet.addRow(values);
    });
    worksheet.columns.forEach((column) => {
      column.width = 24;
    });
    applyExcelThinBorders(worksheet);
    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `${title}.xlsx`);
  };

  const save = () => {
    setSaving(true);
    setErrors({});
    axios.put(COMPLAINTS_UPDATE + selected.id, {
      status: Number(nextStatus),
      outcome_note: outcome,
    }).then((res) => {
      if (res.status === 200 && res.data.success) {
        toast.success(COMPLAINT_UPDATED);
        setSelected(null);
        setErrors({});
        load();
      }
    }).catch((err) => {
      if (err.response?.status === 422) {
        setErrors(err.response.data.error || {});
        toast.error(err.response.data.msg);
      }
    }).finally(() => setSaving(false));
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="row">
        <div className="col-lg-6">
          <h5 className="page-title">{title}</h5>
          <ol className="breadcrumb">
            <li>
              <div className="breadcrumb_f">
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                </div>
                <div><Link to="/dashboard"><span>Dashboard</span></Link></div>
              </div>
            </li>
            <li><span className="active_breadcrumb">{title}</span></li>
          </ol>
        </div>
        <div className="col-lg-6">
          <div className="pt-btn">
            <button type="button" className="btn rounded-pill btn-primary excel-btn ibutton" disabled={loading} onClick={exportExcel}>
              <span className="tf-icons bx bxs-spreadsheet"></span>
              &nbsp; Excel
            </button>
          </div>
        </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="filter-card">
          <h6 className="filter-card-title">Search &amp; Filter</h6>
          <div className="row g-3 align-items-end">
            {!isChapterAdmin && (
              <div className="col-12 col-md-4">
                <label className="filter-label">Chapter</label>
                <Select
                  menuPortalTarget={document.body}
                  styles={glassSelectStyles}
                  options={chapters}
                  isClearable
                  value={chapters.find((item) => item.value === Number(chapterId)) || null}
                  onChange={(selected) => setChapterId(selected?.value || "")}
                />
              </div>
            )}
            <div className="col-12 col-md-3">
              <label className="filter-label">Status</label>
              <Select
                menuPortalTarget={document.body}
                styles={glassSelectStyles}
                options={statusOptions}
                value={statusOptions.find((item) => item.value === String(status)) || statusOptions[0]}
                onChange={(selected) => setStatus(selected?.value || "")}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="filter-label">Search</label>
              <input className="form-control" placeholder="Reference or name" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="card icard sticky-table-card">
          <StickyTable refreshDeps={[rows, loading, caseType]}>
            <table className="table table-striped itable case-list-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Chapter</th>
                  <th>Filed by</th>
                  {!isGrievance && <th>About</th>}
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <Loader loading={loading} colspan={isGrievance ? "5" : "6"} />
                ) : visibleRows.length ? (
                  visibleRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.reference_no}</td>
                      <td>{row.chapter_name}</td>
                      <td>{row.filed_by_name}</td>
                      {!isGrievance && <td>{row.about_user_name}</td>}
                      <td>
                        <span className={`badge case-status ${Number(row.status) === 3 ? "is-closed" : Number(row.status) === 2 ? "is-review" : "is-open"}`}>
                          {row.status_text || row.status_name}
                        </span>
                      </td>
                      <td>
                        {hasPermission("complaints-update") && (
                          <button
                            type="button"
                            className="badge bg-label-success me-1 border-0"
                            title="Edit"
                            onClick={() => openCase(row.id)}
                          >
                            <i className="bx bx-pencil"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <NoDataFound colspan={isGrievance ? "5" : "6"} message={isGrievance ? "No grievances found" : "No complaints found"} />
                )}
              </tbody>
            </table>
          </StickyTable>
        </div>
      </div>
      <Modal
        show={!!selected}
        centered
        backdrop="static"
        onHide={() => { setSelected(null); setErrors({}); }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal_title1">{selected?.reference_no}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <div className="card-body py-0 case-review">
              <div className="case-review-summary">
                <p className="case-review-meta">
                  <strong>{selected.case_type_name}</strong>
                  <span>{selected.chapter_name}</span>
                </p>
                <p className="case-review-people">
                  Filed by <strong>{selected.filed_by_name}</strong>
                  {!isGrievance && <> about <strong>{selected.about_user_name}</strong></>}
                </p>
                <p className="case-review-note">{selected.note}</p>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="status">
                      Status<span className="text-danger">*</span>
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={glassSelectStyles}
                      name="status"
                      options={reviewStatusOptions}
                      value={reviewStatusOptions.find((item) => item.value === String(nextStatus)) || null}
                      onChange={(selected) => setNextStatus(selected?.value || "")}
                    />
                    <ValidationMessage error={errors} name="status" />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="outcome_note">
                      Outcome note{String(nextStatus) === "3" && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                      className="form-control"
                      id="outcome_note"
                      name="outcome_note"
                      rows="4"
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                    />
                    <ValidationMessage error={errors} name="outcome_note" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-success isubmit_button" disabled={saving} onClick={save}>
            Update
          </button>
          <button type="button" className="btn btn-secondary ibutton" onClick={() => { setSelected(null); setErrors({}); }}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ComplaintsList;
